export function MiniRouteChart({
  points,
  lineColor,
  glowColor,
}: Readonly<{
  points: Array<[number, number]>;
  lineColor: string;
  glowColor: string;
}>) {
  if (points.length < 2) return null;

  const W = 320;
  const H = 100;
  const PAD = 20;

  const scaled = points.map(([x, y]) => [PAD + x * (W - 2 * PAD), PAD + y * (H - 2 * PAD)]);
  const pathD = scaled.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`).join(" ");
  const start = scaled[0];
  const end = scaled.at(-1);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="absolute inset-0 h-full w-full"
      preserveAspectRatio="none"
      aria-hidden={true}
    >
      <defs>
        <filter id={`glow-${lineColor}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Subtle grid lines */}
      <g stroke="rgba(255,255,255,0.04)" strokeWidth="0.5">
        {[0.25, 0.5, 0.75].map((t) => (
          <line key={`h-${t}`} x1="0" y1={H * t} x2={W} y2={H * t} />
        ))}
        {[0.2, 0.4, 0.6, 0.8].map((t) => (
          <line key={`v-${t}`} x1={W * t} y1="0" x2={W * t} y2={H} />
        ))}
      </g>

      {/* Route glow */}
      <path
        d={pathD}
        fill="none"
        stroke={glowColor}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#glow-${lineColor})`}
      />
      {/* Route line */}
      <path d={pathD} fill="none" stroke={lineColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

      {/* Start marker */}
      {start && (
        <>
          <circle cx={start[0]} cy={start[1]} r="5" fill="rgba(20,184,166,0.3)" stroke="#14b8a6" strokeWidth="1.5" />
          <circle cx={start[0]} cy={start[1]} r="2.5" fill="#14b8a6" />
        </>
      )}

      {/* End marker */}
      {end && (
        <>
          <circle cx={end[0]} cy={end[1]} r="5" fill="rgba(245,158,11,0.3)" stroke="#f59e0b" strokeWidth="1.5" />
          <circle cx={end[0]} cy={end[1]} r="2.5" fill="#f59e0b" />
        </>
      )}

      {/* Mid waypoints */}
      {scaled.slice(1, -1).map(([x, y]) => (
        <circle
          key={`wp-${x?.toFixed(2)}-${y?.toFixed(2)}`}
          cx={x}
          cy={y}
          r="3"
          fill="rgba(4,124,199,0.25)"
          stroke={lineColor}
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}
